import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import apiClient from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../utils/permissions";
import NumericInput from "./ui/NumericInput";

function formatNumber(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("he-IL");
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("he-IL");
}

function formatPct(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function formatILS(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(n);
}

function UtilizationBar({ pct }) {
  const clamped = Math.min(100, Math.max(0, Number(pct) || 0));
  const color = clamped > 80 ? "#ef4444" : clamped > 60 ? "#f59e0b" : "#22c55e";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#e5e7eb", overflow: "hidden" }}>
        <div style={{ width: `${clamped}%`, height: "100%", borderRadius: 4, background: color }} />
      </div>
      <span style={{ fontSize: 12, color: "#6b7280", minWidth: 36 }}>{formatPct(clamped)}</span>
    </div>
  );
}

function SummaryCard({ label, value, sub, children }) {
  return (
    <div style={styles.card}>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#1e3a5f" }}>{value}</div>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: "#6b7280" }}>{sub}</div>}
      {children}
    </div>
  );
}

// ── Draw / Repay Modal ─────────────────────────────────────────────────────────
function DrawRepayModal({ facility, mode, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isDrawMode = mode === "draw";
  const maxAmount = isDrawMode
    ? facility.available
    : facility.drawn;

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const endpoint = isDrawMode
        ? `/credit-facilities/${facility._id}/draw`
        : `/credit-facilities/${facility._id}/repay`;
      const { data } = await apiClient.post(endpoint, { amount: Number(amount) });
      onDone(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={{ margin: 0 }}>{isDrawMode ? "משיכת אשראי" : "החזר אשראי"} — {facility.lender}</h3>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={{ marginBottom: 16, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: "#6b7280" }}>מסגרת כוללת</span>
            <span style={{ fontWeight: 600 }}>{formatILS(facility.principal)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: "#6b7280" }}>נמשך כרגע</span>
            <span style={{ fontWeight: 600, color: "#ef4444" }}>{formatILS(facility.drawn)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#6b7280" }}>זמין למשיכה</span>
            <span style={{ fontWeight: 600, color: "#22c55e" }}>{formatILS(facility.available)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <label style={styles.label}>
            {isDrawMode ? "סכום למשיכה" : "סכום להחזר"} ({facility.currency || "ILS"})
          </label>
          <NumericInput
            style={styles.input}
            min="1"
            max={maxAmount}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`עד ${formatNumber(maxAmount)}`}
            required
          />
          {amount && (
            <div style={{ fontSize: 12, color: "#6b7280", padding: "8px 10px", background: "#f0f9ff", borderRadius: 6 }}>
              {isDrawMode
                ? `לאחר משיכה: נמשך ${formatILS(facility.drawn + Number(amount))} | זמין ${formatILS(facility.available - Number(amount))}`
                : `לאחר החזר: נמשך ${formatILS(Math.max(0, facility.drawn - Number(amount)))} | זמין ${formatILS(facility.available + Number(amount))}`
              }
            </div>
          )}
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.modalActions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>ביטול</button>
            <button
              type="submit"
              style={{ ...styles.saveBtn, background: isDrawMode ? "#1e3a5f" : "#15803d" }}
              disabled={saving}
            >
              {saving ? "מעדכן..." : isDrawMode ? "משוך" : "החזר"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function CreditFacilitiesPage({ onUnauthorized, canEdit }) {
  const user = useCurrentUser();
  const canViewCfKpis = can(user, 'creditFacilities.viewKpis');
  const canViewCards  = can(user, 'creditFacilities.viewCards');
  const canDraw       = can(user, 'creditFacilities.draw');
  const canRepay      = can(user, 'creditFacilities.repay');
  const [facilities, setFacilities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // { facility, mode }
  const navigate = useNavigate();

  function loadData() {
    setLoading(true);
    setError(null);
    Promise.all([
      apiClient.get(`/credit-facilities`),
      apiClient.get(`/credit-facilities/summary`),
    ])
      .then(([fRes, sRes]) => {
        setFacilities(Array.isArray(fRes.data) ? fRes.data : []);
        setSummary(sRes.data || null);
      })
      .catch((err) => {
        if (err.response?.status === 401 && onUnauthorized) onUnauthorized();
        else setError(err.response?.data?.error || err.message);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadData(); }, []);

  function handleDrawRepayDone(updatedFacility) {
    setFacilities((prev) =>
      prev.map((f) => (f._id === updatedFacility._id ? updatedFacility : f))
    );
    // Refresh summary
    apiClient.get(`/credit-facilities/summary`).then((r) => setSummary(r.data)).catch(() => {});
    setActiveModal(null);
  }

  const chartData = useMemo(
    () => facilities.map((f) => ({
      name: f.lender || f.borrowerCompany || "ללא שם",
      drawn: f.drawn || 0,
      available: f.available || 0,
    })),
    [facilities]
  );

  if (loading) return <div style={styles.page}><p style={{ color: "#6b7280" }}>טוען מסגרות אשראי...</p></div>;
  if (error) return <div style={styles.page}><div style={styles.errorBox}>{error}</div></div>;

  return (
    <div style={styles.page}>
      <h2 style={{ margin: "0 0 16px 0" }}>מסגרות אשראי</h2>

      {/* Summary cards */}
      {canViewCfKpis && summary && (
        <div style={styles.cards}>
          <SummaryCard
            label="סה״כ מחויבויות"
            value={formatILS(summary.totalCommitments)}
            sub={`${summary.count} מסגרות פעילות`}
          />
          <SummaryCard
            label="סה״כ נמשך"
            value={formatILS(summary.totalDrawn)}
            sub="יתרת חוב פעיל"
          />
          <SummaryCard
            label="סה״כ זמין"
            value={formatILS(summary.totalAvailable)}
            sub="ניתן למשיכה"
          />
          <SummaryCard label="ניצולת כוללת" value={formatPct(summary.overallUtilizationPct)}>
            <div style={{ marginTop: 8 }}>
              <UtilizationBar pct={summary.overallUtilizationPct} />
            </div>
            {summary.totalMonthlyCost > 0 && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                עלות חודשית כוללת: {formatILS(summary.totalMonthlyCost)}
              </div>
            )}
          </SummaryCard>
        </div>
      )}

      {/* Facility cards */}
      {facilities.length === 0 ? (
        <div style={styles.emptyState}>
          <p>אין מסגרות אשראי פעילות.</p>
          <p style={{ fontSize: 13, color: "#6b7280" }}>
            צור הלוואה חדשה עם סיווג "מסגרת אשראי" כדי שתופיע כאן.
          </p>
        </div>
      ) : canViewCards ? (
        <div style={styles.facilityGrid}>
          {facilities.map((f) => (
            <div key={f._id} style={styles.facilityCard}>
              {/* Card header — click to go to loan detail */}
              <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, cursor: "pointer" }}
                onClick={() => navigate(`/loan/${f._id}`)}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{f.lender || "מלווה לא ידוע"}</div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>{f.borrowerCompany || ""}</div>
                </div>
                <span style={{ ...styles.statusBadge, background: f.status === "active" ? "#dcfce7" : "#f3f4f6", color: f.status === "active" ? "#15803d" : "#6b7280" }}>
                  {f.status === "active" ? "פעיל" : "הסתיים"}
                </span>
              </div>

              {/* Commitment */}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: "#6b7280" }}>מסגרת: </span>
                <span style={{ fontWeight: 600 }}>{formatILS(f.principal)} {f.currency !== "ILS" ? f.currency : ""}</span>
              </div>

              {/* Drawn / Available */}
              <div style={{ marginBottom: 4, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#ef4444" }}>נמשך: {formatILS(f.drawn)}</span>
                <span style={{ color: "#22c55e" }}>זמין: {formatILS(f.available)}</span>
              </div>
              <UtilizationBar pct={f.utilizationPct} />

              {/* Monthly cost breakdown */}
              {f.monthlyCostBreakdown && f.monthlyCostBreakdown.totalMonthlyCost > 0 && (
                <div style={{ marginTop: 8, padding: "8px 10px", background: "#f9fafb", borderRadius: 6, fontSize: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4, color: "#374151" }}>עלות חודשית משוערת:</div>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>
                    <span>ריבית על מנוצל:</span>
                    <span>{formatILS(f.monthlyCostBreakdown.monthlyInterest)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>
                    <span>עמלת אי-ניצול:</span>
                    <span>{formatILS(f.monthlyCostBreakdown.monthlyUnusedFee)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600, marginTop: 4, borderTop: "1px solid #e5e7eb", paddingTop: 4 }}>
                    <span>סה״כ:</span>
                    <span>{formatILS(f.monthlyCostBreakdown.totalMonthlyCost)}</span>
                  </div>
                </div>
              )}

              {/* Meta row */}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280", borderTop: "1px solid #f3f4f6", paddingTop: 8, marginTop: 8 }}>
                <span>פירעון: {formatDate(f.maturityDate)}</span>
                <span>ריבית: {f.interestRate != null ? formatPct(f.interestRate) : "—"}</span>
              </div>

              {/* Action buttons */}
              {(canDraw || canRepay) && f.status === "active" && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  {canDraw && <button
                    style={{ ...styles.actionBtn, flex: 1, background: "#eff6ff", color: "#1d4ed8" }}
                    onClick={() => setActiveModal({ facility: f, mode: "draw" })}
                    disabled={f.available <= 0}
                  >
                    משוך אשראי
                  </button>}
                  {canRepay && <button
                    style={{ ...styles.actionBtn, flex: 1, background: "#f0fdf4", color: "#15803d" }}
                    onClick={() => setActiveModal({ facility: f, mode: "repay" })}
                    disabled={f.drawn <= 0}
                  >
                    החזר
                  </button>}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {/* Utilization chart */}
      {chartData.length > 0 && (
        <div style={styles.chartBox}>
          <h3 style={{ margin: "0 0 12px 0" }}>ניצולת לפי מסגרת</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 16, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={formatNumber} />
              <Tooltip formatter={(v) => formatILS(v)} contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Legend formatter={(v) => v === "drawn" ? "נמשך" : "זמין"} />
              <Bar dataKey="drawn" stackId="a" fill="#fca5a5" name="drawn">
                {chartData.map((_, i) => <Cell key={i} fill="#fca5a5" />)}
              </Bar>
              <Bar dataKey="available" stackId="a" fill="#86efac" radius={[4, 4, 0, 0]} name="available">
                {chartData.map((_, i) => <Cell key={i} fill="#86efac" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {activeModal && (
        <DrawRepayModal
          facility={activeModal.facility}
          mode={activeModal.mode}
          onClose={() => setActiveModal(null)}
          onDone={handleDrawRepayDone}
        />
      )}
    </div>
  );
}

const styles = {
  page: { padding: 24, display: "flex", flexDirection: "column", gap: 16, direction: "rtl" },
  cards: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 },
  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "16px 20px" },
  facilityGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 },
  facilityCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 16 },
  chartBox: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 16 },
  statusBadge: { fontSize: 12, fontWeight: 500, padding: "3px 9px", borderRadius: 6 },
  emptyState: { background: "#f9fafb", border: "1px dashed #d1d5db", borderRadius: 8, padding: 32, textAlign: "center" },
  errorBox: { background: "#fee2e2", color: "#b91c1c", padding: "10px 14px", borderRadius: 6 },
  actionBtn: { border: "none", borderRadius: 6, padding: "7px 0", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal: { background: "#fff", borderRadius: 10, padding: 24, width: 400, direction: "rtl" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  label: { fontSize: 13, color: "#374151" },
  input: { padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14, width: "100%", boxSizing: "border-box" },
  error: { background: "#fee2e2", color: "#b91c1c", padding: "8px 12px", borderRadius: 6, fontSize: 13 },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 },
  cancelBtn: { background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer" },
  saveBtn: { color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer" },
  closeBtn: { background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#6b7280" },
};
